// EditProduct.js - Complete with unique keys and gallery deletion fix
import "./EditProduct.css"
import { useRef, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import { useSelector } from "react-redux";
import { UilCalculator, UilTrashAlt, UilCheck } from "@iconscout/react-unicons";
import ColorPreviewCanvas from "../../Components/ColorPreviewImage/ColorPreviewCanvas";
import { toProxiedUrl } from "../../utils/imageProxyUtils";
import 'react-toastify/dist/ReactToastify.css';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4000/api",
});

const getFrontImage = (images = []) =>
  images.find(img => img.type === "front") || images[0] || null;

const getBackImage = (images = []) =>
  images.find(img => img.type === "back") || images[1] || null;

const slugifyProductIdPart = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const EditProduct = () => {
  const { product_id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const navigate = useNavigate();
  const { currentUser } = useSelector((state) => state.auth);

  useEffect(() => {
    const rawToken = currentUser?.token || null;
    const refreshToken = currentUser?.refreshToken || null;
    if (rawToken) {
      const token = rawToken.startsWith("Bearer ") ? rawToken : `Bearer ${rawToken}`;
      api.defaults.headers.common["Authorization"] = token;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
    if (refreshToken) {
      api.defaults.headers.common["x-refresh-token"] = refreshToken;
    } else {
      delete api.defaults.headers.common["x-refresh-token"];
    }
  }, [currentUser]);

  useEffect(() => {
    api.get("/raw-materials/dropdown")
      .then((res) => setRawMaterials(res.data.rawMaterials || []))
      .catch((err) => console.error("Erreur de chargement des matières premières", err));
  }, []);

  const adultSizes = ["XS", "S", "M", "L", "XL", "XXL"];
  const childSizes = ["4", "6", "8", "10", "12", "14", "16"];

  const [selectedSizeTypes, setSelectedSizeTypes] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [newColor, setNewColor] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [fournitureInput, setFournitureInput] = useState("");
  const [displayedColor, setDisplayedColor] = useState("");
  const [packValues, setPackValues] = useState({});
  
  const [formData, setFormData] = useState({
    _id: "",
    fournisseur: "",
    categoryId: "",
    category: "",
    subcategory: "",
    subcategoryId: "",
    title: "",
    description: "",
    purchase: 0,
    tags: [],
    patronnage: "",
    fourniture: [],
    colorsAndSizes: {},
    sizesOnly: {},
    pack: [],
    isFinishedProduct: false,
    fabricRawMaterialId: "",
    fabricConsumption: {},
    isPublishedB2B: true,
    isPublishedAdmin: true,
  });
  const [rawMaterials, setRawMaterials] = useState([]);

  // Single source of truth for all images (front, back, gallery)
  const [existingImages, setExistingImages] = useState([]);
  const [frontImageFile, setFrontImageFile] = useState(null);
  const [backImageFile, setBackImageFile] = useState(null);
  const [newGalleryFiles, setNewGalleryFiles] = useState([]);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [frontLoaded, setFrontLoaded] = useState(false);
  const [backLoaded, setBackLoaded] = useState(false);

  const frontCanvasRef = useRef(null);
  const backCanvasRef = useRef(null);

  // Derive gallery images from existingImages
  const galleryImages = existingImages.filter(img => img.type && img.type.startsWith("gallery"));

  const extractImageUrl = (img) => {
    if (!img) return null;
    if (typeof img === 'string') return img;
    if (img.url?.url && typeof img.url.url === 'string') return img.url.url;
    if (img.url && typeof img.url === 'string') return img.url;
    if (img.original?.url && typeof img.original.url === 'string') return img.original.url;
    if (img.original && typeof img.original === 'string') return img.original;
    if (img.medium?.url && typeof img.medium.url === 'string') return img.medium.url;
    if (img.medium && typeof img.medium === 'string') return img.medium;
    if (img.thumbnail?.url && typeof img.thumbnail.url === 'string') return img.thumbnail.url;
    if (img.thumbnail && typeof img.thumbnail === 'string') return img.thumbnail;
    return null;
  };

  const getDisplayImageUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    if (url.startsWith('blob:') || url.startsWith('data:image')) return url;
    return toProxiedUrl(url);
  };

  const getExistingImageKey = (img) => {
    if (!img) return "";
    return img._id || extractImageUrl(img) || `${img.type || "image"}_${img.order || ""}_${img.originalName || ""}`;
  };

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      if (frontPreview?.startsWith('blob:')) URL.revokeObjectURL(frontPreview);
      if (backPreview?.startsWith('blob:')) URL.revokeObjectURL(backPreview);
      newGalleryFiles.forEach(f => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    };
  }, [frontPreview, backPreview, newGalleryFiles]);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await api.get("/categories");
        setCategories(response.data.categories);
      } catch (error) {
        console.error(error);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    if (!formData.categoryId && !formData.category) {
      setSubcategories([]);
      return;
    }

    const cat = categories.find(c => String(c._id) === String(formData.categoryId))
      || categories.find(c => c.title === formData.category);

    if (cat && (!formData.categoryId || formData.category !== cat.title)) {
      setFormData(prev => ({
        ...prev,
        categoryId: cat._id,
        category: cat.title,
      }));
    }

    if (!cat?._id) {
      setSubcategories([]);
      return;
    }

    const loadSubcategories = async () => {
      try {
        const response = await api.get(`/subcategories?categoryId=${cat._id}`);
        const subs = response.data.subcategories || [];
        setSubcategories(subs);

        // Pre-migration products may have a subcategory title but no id yet.
        if (!formData.subcategoryId && formData.subcategory) {
          const match = subs.find(sub => sub.title === formData.subcategory);
          if (match) {
            setFormData(prev => ({ ...prev, subcategoryId: match._id }));
          }
        }
      } catch (error) {
        console.error("Erreur de chargement des sous-catégories", error);
        setSubcategories([]);
      }
    };
    loadSubcategories();
  }, [formData.categoryId, formData.category, categories]);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(`/products/${product_id}`);
        const productData = res.data.product;
        setProduct(productData);

        setFormData({
          _id: productData._id || "",
          fournisseur: productData.fournisseur || "",
          categoryId: productData.categoryId || "",
          category: productData.category || "",
          subcategory: productData.subcategory || "",
          subcategoryId: productData.subcategoryId || "",
          title: productData.title || "",
          description: productData.description || "",
          purchase: productData.purchase || 0,
          tags: productData.tags || [],
          patronnage: productData.patronnage || "",
          fourniture: productData.fourniture || [],
          colorsAndSizes: productData.colorsAndSizes || {},
          sizesOnly: productData.sizesOnly || {},
          pack: productData.pack || [],
          isFinishedProduct: productData.isFinishedProduct || false,
          fabricRawMaterialId: productData.fabricRawMaterialId || "",
          fabricConsumption: productData.fabricConsumption || {},
          isPublishedB2B: productData.isPublishedB2B !== false,
          isPublishedAdmin: productData.isPublishedAdmin !== false,
        });

        const allImgs = productData.imgs || [];
        setExistingImages(allImgs);

        const frontImg = getFrontImage(allImgs);
        const backImg = getBackImage(allImgs);
        const frontUrl = extractImageUrl(frontImg);
        const backUrl = extractImageUrl(backImg);
        setFrontPreview(frontUrl);
        setBackPreview(backUrl);

        let existingSizes = [];
        if (productData.isFinishedProduct) {
          existingSizes = Object.keys(productData.sizesOnly || {});
          existingSizes.sort((a, b) => {
            const order = ["XS", "S", "M", "L", "XL", "XXL", "4", "6", "8", "10", "12", "14", "16"];
            return order.indexOf(a) - order.indexOf(b);
          });
        } else {
          const allSizes = new Set();
          Object.values(productData.colorsAndSizes || {}).forEach(cs => {
            Object.keys(cs).forEach(s => allSizes.add(s));
          });
          existingSizes = Array.from(allSizes);
        }
        
        const types = [];
        if (existingSizes.some(s => adultSizes.includes(s))) types.push('adult');
        if (existingSizes.some(s => childSizes.includes(s))) types.push('child');
        setSelectedSizeTypes(types);
        setSelectedSizes(existingSizes);
        
        if (productData.isFinishedProduct && existingSizes.length > 0 && productData.pack.length !== existingSizes.length) {
          const newPack = new Array(existingSizes.length).fill(0);
          setFormData(prev => ({ ...prev, pack: newPack }));
        }
        
        if (!productData.isFinishedProduct && productData.colorsAndSizes && Object.keys(productData.colorsAndSizes).length > 0) {
          setDisplayedColor(Object.keys(productData.colorsAndSizes)[0]);
        }

        if (frontUrl) {
          const preload = new Image();
          preload.onload = () => setFrontLoaded(true);
          preload.onerror = () => setFrontLoaded(true);
          preload.src = frontUrl;
        } else setFrontLoaded(true);
        
        if (backUrl) {
          const preload = new Image();
          preload.onload = () => setBackLoaded(true);
          preload.onerror = () => setBackLoaded(true);
          preload.src = backUrl;
        } else setBackLoaded(true);

        setLoading(false);
      } catch (err) {
        console.error(err);
        toast.error("Erreur lors du chargement");
        setLoading(false);
      }
    };
    if (product_id) fetchProduct();
  }, [product_id]);

  // Preload blob previews
  useEffect(() => {
    if (frontPreview?.startsWith('blob:') && !frontLoaded) {
      const preload = new Image();
      preload.onload = () => setFrontLoaded(true);
      preload.onerror = () => setFrontLoaded(true);
      preload.src = frontPreview;
    }
  }, [frontPreview, frontLoaded]);

  useEffect(() => {
    if (backPreview?.startsWith('blob:') && !backLoaded) {
      const preload = new Image();
      preload.onload = () => setBackLoaded(true);
      preload.onerror = () => setBackLoaded(true);
      preload.src = backPreview;
    }
  }, [backPreview, backLoaded]);

  // Apply color to preview
  useEffect(() => {
    if (!formData.isFinishedProduct && displayedColor && frontLoaded && backLoaded) {
      const timer = setTimeout(() => {
        frontCanvasRef.current?.applyColor?.(displayedColor);
        backCanvasRef.current?.applyColor?.(displayedColor);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [displayedColor, formData.isFinishedProduct, frontLoaded, backLoaded]);

  // Handlers
  const handleFrontImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFrontImageFile(file);
      setFrontLoaded(false);
      if (frontPreview?.startsWith('blob:')) URL.revokeObjectURL(frontPreview);
      setFrontPreview(URL.createObjectURL(file));
    }
  };

  const handleBackImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBackImageFile(file);
      setBackLoaded(false);
      if (backPreview?.startsWith('blob:')) URL.revokeObjectURL(backPreview);
      setBackPreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newImages = files.map((file, idx) => ({
      id: `new_${Date.now()}_${idx}`,
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
    }));

    setNewGalleryFiles(prev => [...prev, ...newImages]);
    toast.success(`${files.length} image(s) ajoutée(s) à la galerie`);
    e.target.value = null;
  };

  const handleDeleteExistingGalleryImage = (imageKey) => {
    if (!imageKey) {
      toast.error("Impossible d'identifier cette image");
      return;
    }
    setExistingImages(prev => prev.filter(img => getExistingImageKey(img) !== imageKey));
    toast.info("Image galerie supprimée");
  };

  const handleDeleteNewGalleryImage = (imageId) => {
    setNewGalleryFiles(prev => {
      const removed = prev.find(img => img.id === imageId);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter(img => img.id !== imageId);
    });
    toast.info("Image supprimée");
  };

  const formatFileSize = (size) => size < 1024 * 1024
    ? `${Math.max(1, Math.round(size / 1024))} KB`
    : `${(size / (1024 * 1024)).toFixed(1)} MB`;

  const getFileNameFromUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    try {
      const parts = url.split('/');
      return parts[parts.length - 1]?.substring(0, 30) || 'image';
    } catch { return 'image'; }
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !formData.tags.includes(trimmed)) {
      setFormData({ ...formData, tags: [...formData.tags, trimmed] });
      setTagInput("");
    }
  };

  const handleRemoveTag = (indexToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter((_, idx) => idx !== indexToRemove) });
  };

  const handleAddFourniture = () => {
    const trimmed = fournitureInput.trim();
    if (trimmed && !formData.fourniture.includes(trimmed)) {
      setFormData({ ...formData, fourniture: [...formData.fourniture, trimmed] });
      setFournitureInput("");
    }
  };

  const handleRemoveFourniture = (indexToRemove) => {
    setFormData({ ...formData, fourniture: formData.fourniture.filter((_, idx) => idx !== indexToRemove) });
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    const category = categories.find(cat => String(cat._id) === String(categoryId));
    setFormData(prev => ({
      ...prev,
      categoryId,
      category: category?.title || "",
      subcategory: "",
      subcategoryId: "",
    }));
  };

  const handleSubcategoryChange = (e) => {
    const selected = subcategories.find(sub => String(sub._id) === e.target.value);
    setFormData(prev => ({ ...prev, subcategoryId: selected?._id || "", subcategory: selected?.title || "" }));
  };

  const handleSizeTypeToggle = (type) => {
    let newSelectedTypes;
    if (selectedSizeTypes.includes(type)) {
      newSelectedTypes = selectedSizeTypes.filter(t => t !== type);
      const sizesToRemove = type === 'adult' ? adultSizes : childSizes;
      const remainingSizes = selectedSizes.filter(s => !sizesToRemove.includes(s));
      setSelectedSizes(remainingSizes);
      
      if (formData.isFinishedProduct) {
        const updated = { ...formData.sizesOnly };
        sizesToRemove.forEach(s => delete updated[s]);
        setFormData(prev => ({ ...prev, sizesOnly: updated }));
      } else {
        const updatedColors = { ...formData.colorsAndSizes };
        Object.keys(updatedColors).forEach(color => {
          sizesToRemove.forEach(s => delete updatedColors[color][s]);
        });
        setFormData(prev => ({ ...prev, colorsAndSizes: updatedColors }));
      }
    } else {
      newSelectedTypes = [...selectedSizeTypes, type];
    }
    setSelectedSizeTypes(newSelectedTypes);
  };

  const handleSizeChange = (sizeValue) => {
    const isAdding = !selectedSizes.includes(sizeValue);
    const newSelectedSizes = isAdding
      ? [...selectedSizes, sizeValue]
      : selectedSizes.filter(s => s !== sizeValue);
    const sortedSizes = [...newSelectedSizes].sort((a, b) => {
      const aIsAdult = adultSizes.includes(a);
      const bIsAdult = adultSizes.includes(b);
      if (aIsAdult && !bIsAdult) return -1;
      if (!aIsAdult && bIsAdult) return 1;
      if (aIsAdult && bIsAdult) {
        const order = ["XS","S","M","L","XL","XXL"];
        return order.indexOf(a) - order.indexOf(b);
      } else {
        return Number(a) - Number(b);
      }
    });

    const originalSizesOrder = product
      ? (product.isFinishedProduct
          ? Object.keys(product.sizesOnly || {})
          : (() => {
              const set = new Set();
              Object.values(product.colorsAndSizes || {}).forEach(cs => Object.keys(cs).forEach(k => set.add(k)));
              return Array.from(set);
            })())
      : [];
    const originalPackBySize = {};
    originalSizesOrder.forEach((size, i) => { originalPackBySize[size] = product?.pack?.[i] ?? 0; });

    const newPack = [];
    for (let i = 0; i < sortedSizes.length; i++) {
      const size = sortedSizes[i];
      const currentPackValue = formData.pack[selectedSizes.indexOf(size)] ?? 0;
      const originalPackValue = originalPackBySize[size] ?? 0;
      newPack[i] = currentPackValue > 0 ? currentPackValue : originalPackValue;
    }
    setFormData(prev => ({ ...prev, pack: newPack }));
    setSelectedSizes(sortedSizes);

    if (formData.isFinishedProduct) {
      const updated = { ...formData.sizesOnly };
      if (!isAdding) delete updated[sizeValue];
      else updated[sizeValue] = product?.sizesOnly?.[sizeValue] || 0;
      setFormData(prev => ({ ...prev, sizesOnly: updated }));
    } else {
      const updatedColors = { ...formData.colorsAndSizes };
      Object.keys(updatedColors).forEach(color => {
        const newSizes = {};
        sortedSizes.forEach(s => {
          newSizes[s] = updatedColors[color]?.[s] ?? product?.colorsAndSizes?.[color]?.[s] ?? 0;
        });
        updatedColors[color] = newSizes;
      });
      setFormData(prev => ({ ...prev, colorsAndSizes: updatedColors }));
    }
  };

  const handleAddColor = () => {
    if (formData.isFinishedProduct) {
      toast.error("Les produits finis n'ont pas de couleurs");
      return;
    }
    if (!newColor) {
      toast.error("Veuillez entrer une couleur");
      return;
    }
    if (formData.colorsAndSizes.hasOwnProperty(newColor)) {
      toast.error("Cette couleur existe déjà");
      return;
    }
    const original = product?.colorsAndSizes?.[newColor];
    const initialSizes = {};
    selectedSizes.forEach(size => {
      initialSizes[size] = original?.[size] || 0;
    });
    setFormData(prev => ({
      ...prev,
      colorsAndSizes: { ...prev.colorsAndSizes, [newColor]: initialSizes }
    }));
    setNewColor("");
    if (!displayedColor) setDisplayedColor(newColor);
    toast.success(`Couleur ${newColor} ajoutée`);
  };

  const handleRemoveColor = (colorName) => {
    const updated = { ...formData.colorsAndSizes };
    delete updated[colorName];
    setFormData({ ...formData, colorsAndSizes: updated });
    if (displayedColor === colorName) setDisplayedColor("");
    toast.info(`Couleur ${colorName} supprimée`);
  };

  const handleQuantityChange = (colorName, size, value) => {
    setFormData(prev => ({
      ...prev,
      colorsAndSizes: {
        ...prev.colorsAndSizes,
        [colorName]: { ...prev.colorsAndSizes[colorName], [size]: Number(value) || 0 }
      }
    }));
  };

  const handlePackChange = (index, value) => {
    const newPack = [...formData.pack];
    newPack[index] = Number(value) || 0;
    setFormData({ ...formData, pack: newPack });
  };

  const getSizeLabel = (size) => {
    if (adultSizes.includes(size)) return size;
    if (childSizes.includes(size)) return `${size} ans`;
    return size;
  };

  const handlePackQuantityChangeForColor = (colorName) => {
    const nbPacks = packValues[colorName];
    if (!nbPacks || nbPacks === "") {
      toast.error("Veuillez entrer le nombre de packs reçus");
      return;
    }
    const nbPacksNum = Number(nbPacks);
    if (isNaN(nbPacksNum) || nbPacksNum <= 0) {
      toast.error("Veuillez entrer un nombre valide supérieur à 0");
      return;
    }
    const hasPackDefined = formData.pack.some(qty => qty > 0);
    if (!hasPackDefined) {
      toast.error("Définissez d'abord la composition d'un pack");
      return;
    }

    setFormData(prev => {
      const updatedSizes = { ...prev.colorsAndSizes[colorName] };
      let total = 0;
      selectedSizes.forEach((size, idx) => {
        const newQty = nbPacksNum * (prev.pack[idx] || 0);
        updatedSizes[size] = newQty;
        total += newQty;
      });
      toast.success(`${nbPacksNum} pack(s) → ${total} pièces pour ${colorName}`);
      return {
        ...prev,
        colorsAndSizes: { ...prev.colorsAndSizes, [colorName]: updatedSizes }
      };
    });
  };

  const handleFinishedProductPackQuantityChange = () => {
    const nbPacks = packValues.global;
    if (!nbPacks) {
      toast.error("Veuillez entrer le nombre de packs reçus");
      return;
    }
    const nbPacksNum = Number(nbPacks);
    if (isNaN(nbPacksNum) || nbPacksNum <= 0) {
      toast.error("Nombre invalide");
      return;
    }
    const hasPackDefined = formData.pack.some(qty => qty > 0);
    if (!hasPackDefined) {
      toast.error("Définissez d'abord la composition d'un pack");
      return;
    }
    setFormData(prev => {
      const updated = { ...prev.sizesOnly };
      let total = 0;
      selectedSizes.forEach((size, idx) => {
        const newQty = nbPacksNum * (prev.pack[idx] || 0);
        updated[size] = newQty;
        total += newQty;
      });
      toast.success(`${nbPacksNum} pack(s) → ${total} pièces totales`);
      return { ...prev, sizesOnly: updated };
    });
  };

  const handlePackValueChange = (colorName, value) => {
    setPackValues(prev => ({ ...prev, [colorName]: value }));
  };

  const handleFinishedProductSizeChange = (size, value) => {
    setFormData(prev => ({
      ...prev,
      sizesOnly: { ...prev.sizesOnly, [size]: Number(value) || 0 }
    }));
  };

  const calculateTotalQuantity = () => {
    if (formData.isFinishedProduct) {
      return Object.values(formData.sizesOnly).reduce((s, q) => s + (Number(q) || 0), 0);
    } else {
      let total = 0;
      Object.values(formData.colorsAndSizes).forEach(cs => {
        Object.values(cs).forEach(q => total += Number(q) || 0);
      });
      return total;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const updateData = new FormData();
      updateData.append("title", formData.title);
      updateData.append("description", formData.description);
      updateData.append("purchase", formData.purchase);
      updateData.append("categoryId", formData.categoryId || "");
      updateData.append("category", formData.category);
      updateData.append("subcategory", formData.subcategory);
      updateData.append("subcategoryId", formData.subcategoryId || "");
      updateData.append("tags", JSON.stringify(formData.tags));
      updateData.append("patronnage", formData.patronnage || "");
      updateData.append("fourniture", JSON.stringify(formData.fourniture));
      updateData.append("fabricRawMaterialId", formData.fabricRawMaterialId || "");
      updateData.append("fabricConsumption", JSON.stringify(formData.fabricConsumption || {}));
      updateData.append("isPublishedB2B", formData.isPublishedB2B.toString());
      updateData.append("isPublishedAdmin", formData.isPublishedAdmin.toString());

      if (!formData.isFinishedProduct) {
        const cleaned = {};
        Object.entries(formData.colorsAndSizes).forEach(([color, sizes]) => {
          cleaned[color] = {};
          selectedSizes.forEach(size => {
            cleaned[color][size] = sizes[size] || 0;
          });
        });
        updateData.append("colorsAndSizes", JSON.stringify(cleaned));
      } else {
        const cleanedSizes = {};
        selectedSizes.forEach((size, idx) => {
          cleanedSizes[size] = formData.sizesOnly[size] || formData.pack[idx] || 0;
        });
        updateData.append("sizesOnly", JSON.stringify(cleanedSizes));
        updateData.append("pack", JSON.stringify(formData.pack));
      }

      // Images to keep (from existingImages, excluding those replaced)
      const imagesToKeep = [];
      if (!frontImageFile) {
        const frontImg = getFrontImage(existingImages);
        if (frontImg) imagesToKeep.push(frontImg);
      }
      if (!backImageFile) {
        const backImg = getBackImage(existingImages);
        if (backImg) imagesToKeep.push(backImg);
      }
      // All remaining gallery images (already filtered from existingImages)
      galleryImages.forEach(img => imagesToKeep.push(img));

      updateData.append("existingImages", JSON.stringify(imagesToKeep));

      const imageTypes = [];
      if (frontImageFile) {
        updateData.append("images", frontImageFile);
        imageTypes.push("front");
      }
      if (backImageFile) {
        updateData.append("images", backImageFile);
        imageTypes.push("back");
      }
      newGalleryFiles.forEach((img, idx) => {
        updateData.append("images", img.file);
        imageTypes.push(`gallery-${idx+1}`);
      });
      if (imageTypes.length) {
        updateData.append("imageTypes", JSON.stringify(imageTypes));
      }

      await api.put(`/products/${product_id}`, updateData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Produit mis à jour avec succès!");
      setTimeout(() => navigate("/products"), 2000);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Erreur lors de la mise à jour");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-product-container">
        <div style={{ textAlign: "center", padding: "50px" }}><h2>Chargement...</h2></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="edit-product-container">
        <div style={{ textAlign: "center", padding: "50px" }}><h2>Produit non trouvé</h2></div>
      </div>
    );
  }

  const getAllAvailableSizes = () => {
    const sizes = [];
    if (selectedSizeTypes.includes('adult')) sizes.push(...adultSizes.map(s => ({ type: 'adult', label: s, value: s })));
    if (selectedSizeTypes.includes('child')) sizes.push(...childSizes.map(s => ({ type: 'child', label: `${s} ans`, value: s })));
    return sizes;
  };

  const generateProductId = (fournisseur, category, subcategory, title) => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const formattedSupplier = slugifyProductIdPart(fournisseur);
    const formattedCategory = slugifyProductIdPart(category);
    const formattedSubcategory = slugifyProductIdPart(subcategory);
    const formattedTitle = slugifyProductIdPart(title);
    return `${formattedSupplier}_${formattedCategory}_${formattedSubcategory}_${formattedTitle}_${day}${month}${year}`;
  };

  const handleGenerateId = () => {
    if (!formData.fournisseur) { toast.error("Remplir Fournisseur !!"); return; }
    if (!formData.category) { toast.error("Remplir Catégorie !!"); return; }
    if (!formData.subcategory) { toast.error("Remplir Sous-catégorie !!"); return; }
    if (!formData.title) { toast.error("Remplir le nom du produit !!"); return; }
    const generatedId = generateProductId(formData.fournisseur, formData.category, formData.subcategory, formData.title);
    setFormData(prev => ({ ...prev, _id: generatedId }));
    toast.success("ID généré avec succès!");
  };

  return (
    <div className="edit-product-container">
      <div className="edit-product-grid">
        <div className="edit-product-grid-item">
          <h1 className="edit-product-title">Modifier produit</h1>

          <div className="edit-product-field-group">
            <h4 className="edit-product-label">TYPE DE PRODUIT</h4>
            <div className="edit-product-input">
              <input value={formData.isFinishedProduct ? "Produit fini (sans couleurs)" : "Produit standard (avec couleurs)"} readOnly className="edit-product-text" />
            </div>
          </div>

          <div className="edit-product-field-group">
            <h4 className="edit-product-label">RÉFÉRENCE PRODUIT</h4>
            <div className="edit-product-input">
              <input value={formData._id} readOnly className="edit-product-text" />
              <button type="button" onClick={handleGenerateId} style={{ marginLeft: '8px', padding: '10px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Générer ID</button>
            </div>
          </div>

          <div className="edit-product-field-group">
            <h4 className="edit-product-label">FOURNISSEUR</h4>
            <div className="edit-product-input">
              <input value={product.fournisseur || ""} readOnly className="edit-product-text" />
            </div>
          </div>

          <div className="edit-product-field-group">
            <h4 className="edit-product-label">DESCRIPTION</h4>
            <div className="edit-product-input">
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="edit-product-textarea" placeholder="Description du produit" rows="4" />
            </div>
          </div>

          <div className="edit-product-field-group">
            <h4 className="edit-product-label">PRIX D'ACHAT (HT)</h4>
            <div className="edit-product-input">
              <input value={formData.purchase} onChange={(e) => setFormData({ ...formData, purchase: Number(e.target.value) || 0 })} type="number" step="0.01" className="edit-product-text" placeholder="Prix d'achat (€)" />
            </div>
          </div>

          <div className="edit-product-category-row">
            <div>
              <h4 className="edit-product-label">CATÉGORIE</h4>
              <select className="add-product-input" value={formData.categoryId || ""} onChange={handleCategoryChange}>
                <option value="">Sélectionner catégorie</option>
                {categories?.map((cat) => (<option key={cat._id} value={cat._id}>{cat.title}</option>))}
              </select>
            </div>
            <div>
              <h4 className="edit-product-label">SOUS-CATÉGORIE</h4>
              <select className="add-product-input" value={formData.subcategoryId || ""} onChange={handleSubcategoryChange}>
                <option value="">Sélectionner sous-catégorie</option>
                {subcategories?.map((sub) => (<option key={sub._id} value={sub._id}>{sub.title}</option>))}
              </select>
            </div>
          </div>

          <div className="edit-product-field-group">
            <h4 className="edit-product-label">TYPES DE TAILLES</h4>
            <div className="edit-product-sizes-container">
              <label className={`edit-product-sizes-label ${selectedSizeTypes.includes('adult') ? 'active' : ''}`}>
                <input type="checkbox" checked={selectedSizeTypes.includes('adult')} onChange={() => handleSizeTypeToggle('adult')} />
                Adultes (XS à XXL)
              </label>
              <label className={`edit-product-sizes-label ${selectedSizeTypes.includes('child') ? 'active' : ''}`}>
                <input type="checkbox" checked={selectedSizeTypes.includes('child')} onChange={() => handleSizeTypeToggle('child')} />
                Enfants (4 à 16 ans)
              </label>
            </div>
          </div>

          {selectedSizeTypes.length > 0 && (
            <div className="edit-product-field-group">
              <h4 className="edit-product-label">TAILLES DISPONIBLES</h4>
              <div className="edit-product-sizes-container">
                {getAllAvailableSizes().map((sizeInfo) => (
                  <label key={`${sizeInfo.type}_${sizeInfo.value}`} className="edit-product-sizes-label">
                    <input type="checkbox" value={sizeInfo.value} checked={selectedSizes.includes(sizeInfo.value)} onChange={() => handleSizeChange(sizeInfo.value)} />
                    {sizeInfo.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {selectedSizes.length > 0 && !formData.isFinishedProduct && (
            <div className="edit-product-field-group">
              <h4 className="edit-product-label">COULEURS</h4>
              <div className="edit-product-colors">
                {Object.entries(formData.colorsAndSizes).map(([colorName]) => (
                  <div key={colorName} className="edit-product-color-swatch" style={{ backgroundColor: colorName }} onClick={() => setDisplayedColor(colorName)} title={colorName}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveColor(colorName); }}>&times;</button>
                  </div>
                ))}
              </div>
              <div className="edit-product-color-input">
                <input type="text" placeholder="Ajouter une couleur (ex: red, #FF0000, rgb(255,0,0))" value={newColor} onChange={(e) => setNewColor(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddColor(); } }} />
                <button type="button" onClick={handleAddColor}>Ajouter</button>
              </div>
            </div>
          )}

          {selectedSizes.length > 0 && (
            <div className="edit-product-quantity-container">
              <div className="edit-product-pack-row">
                <h4 className="edit-product-label">COMPOSITION D'UN PACK</h4>
                <div className="edit-product-pack-inputs">
                  {selectedSizes.map((size, index) => (
                    <div className={`edit-product-pack-item ${(formData.pack[index] || 0) > 0 ? 'has-value' : ''}`} key={size}>
                      <h4>{getSizeLabel(size)}:</h4>
                      <input className="edit-product-quantity-input" type="number" value={formData.pack[index] || 0} onChange={(e) => handlePackChange(index, e.target.value)} min="0" placeholder="0" />
                    </div>
                  ))}
                </div>
                <p className="edit-product-helper-text">Nombre de pièces par taille dans un pack</p>
              </div>

              {!formData.isFinishedProduct ? (
                <>
                  {Object.entries(formData.colorsAndSizes).length > 0 ? (
                    <div className="edit-product-colors-quantity-list">
                      {Object.entries(formData.colorsAndSizes).map(([colorName, sizes]) => {
                        const hasQuantities = Object.values(sizes).some(q => q > 0);
                        const totalForColor = Object.values(sizes).reduce((s, q) => s + (Number(q) || 0), 0);
                        return (
                          <div className={`edit-product-color-quantity-card ${hasQuantities ? 'has-stock' : ''}`} key={colorName}>
                            <div className="edit-product-color-quantity-header">
                              <div className="edit-product-color-swatch" style={{ backgroundColor: colorName }} onClick={() => setDisplayedColor(colorName)} />
                              <h4>{colorName}</h4>
                              {hasQuantities && <span className="edit-product-stock-badge">Stock: {totalForColor} pièces</span>}
                            </div>
                            <div className="edit-product-color-quantity-sizes">
                              {selectedSizes.map((size) => (
                                <div className="edit-product-size-quantity-item" key={size}>
                                  <h4>{getSizeLabel(size)}</h4>
                                  <input className="edit-product-quantity-input" type="number" value={formData.colorsAndSizes[colorName]?.[size] ?? 0} min={0} onChange={(e) => handleQuantityChange(colorName, size, e.target.value)} />
                                </div>
                              ))}
                            </div>
                            <div className="edit-product-pack-calculator">
                              <h4>Nombre de packs reçus</h4>
                              <input className="edit-product-quantity-input" type="number" min="0" step="1" value={packValues[colorName] || ""} onChange={(e) => handlePackValueChange(colorName, e.target.value)} placeholder="ex: 5" style={{ width: '80px' }} />
                              <button onClick={() => handlePackQuantityChangeForColor(colorName)}>
                                <UilCalculator size={16} />
                                <span>Calculer</span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="edit-product-no-colors"><p>Veuillez ajouter des couleurs</p></div>
                  )}
                </>
              ) : (
                <div className="edit-product-finished-card">
                  <div className="edit-product-color-quantity-header"><h4>Quantités par taille</h4></div>
                  <div className="edit-product-color-quantity-sizes">
                    {selectedSizes.map((size) => (
                      <div key={size} className="edit-product-size-quantity-item">
                        <h4>{getSizeLabel(size)}</h4>
                        <input className="edit-product-quantity-input" type="number" value={formData.sizesOnly[size] ?? 0} min={0} onChange={(e) => handleFinishedProductSizeChange(size, e.target.value)} />
                      </div>
                    ))}
                  </div>
                  <div className="edit-product-pack-calculator">
                    <h4>Nombre de packs reçus</h4>
                    <input className="edit-product-quantity-input" type="number" min="0" step="1" value={packValues.global || ""} onChange={(e) => handlePackValueChange('global', e.target.value)} placeholder="ex: 5" style={{ width: '80px' }} />
                    <button onClick={handleFinishedProductPackQuantityChange}>
                      <UilCalculator size={16} />
                      <span>Calculer</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="edit-product-field-group">
            <h4 className="edit-product-label">TAGS</h4>
            <div className="edit-product-tag-list">
              {formData.tags.map((tag, index) => (
                <span key={index} className="edit-product-tag-item">{tag}<button type="button" className="edit-product-tag-remove" onClick={() => handleRemoveTag(index)}>&times;</button></span>
              ))}
            </div>
            <div className="edit-product-tag-input">
              <input type="text" placeholder="Ajouter un tag" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAddTag()} />
              <button type="button" onClick={handleAddTag}>Ajouter</button>
            </div>
          </div>

          <div className="edit-product-field-group">
            <h4 className="edit-product-label">PATRONNAGE</h4>
            <input
              type="text"
              placeholder="Référence du patronnage"
              value={formData.patronnage}
              onChange={(e) => setFormData({ ...formData, patronnage: e.target.value })}
            />
          </div>

          <div className="edit-product-field-group">
            <h4 className="edit-product-label">FOURNITURE</h4>
            <div className="edit-product-tag-list">
              {formData.fourniture.map((item, index) => (
                <span key={index} className="edit-product-tag-item">{item}<button type="button" className="edit-product-tag-remove" onClick={() => handleRemoveFourniture(index)}>&times;</button></span>
              ))}
            </div>
            <div className="edit-product-tag-input">
              <input type="text" placeholder="Ajouter une fourniture" value={fournitureInput} onChange={(e) => setFournitureInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAddFourniture()} />
              <button type="button" onClick={handleAddFourniture}>Ajouter</button>
            </div>
          </div>

          <div className="edit-product-field-group">
            <h4 className="edit-product-label">CONSOMMATION MATIÈRE (kg de tissu)</h4>
            <select
              value={formData.fabricRawMaterialId}
              onChange={(e) => setFormData({ ...formData, fabricRawMaterialId: e.target.value })}
            >
              <option value="">Sélectionner une matière première...</option>
              {rawMaterials.map((rm) => (
                <option key={rm._id} value={rm._id}>{rm.name} ({rm.unit})</option>
              ))}
            </select>
            {selectedSizes.length > 0 ? (
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "0.75rem" }}>
                {selectedSizes.map((size) => (
                  <div key={size}>
                    <h4>{getSizeLabel(size)}</h4>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        placeholder="0"
                        style={{ width: "52px" }}
                        value={formData.fabricConsumption[size] ?? ""}
                        onChange={(e) => setFormData({
                          ...formData,
                          fabricConsumption: { ...formData.fabricConsumption, [size]: e.target.value },
                        })}
                      />
                      <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "#94a3b8" }}>KG</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: "0.85rem", opacity: 0.7 }}>Sélectionnez d'abord des tailles pour renseigner la consommation.</p>
            )}
          </div>

          {formData.isFinishedProduct && (
            <div className="edit-product-field-group">
              <h4 className="edit-product-label">PUBLICATION</h4>
              <div style={{ display: "flex", gap: "0.75rem" }}>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, isPublishedB2B: !formData.isPublishedB2B })}
                  style={{ background: formData.isPublishedB2B ? "var(--success, #22c55e)" : undefined }}
                >
                  {formData.isPublishedB2B ? "✔ Publié dans B2B" : "Publier dans B2B"}
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, isPublishedAdmin: !formData.isPublishedAdmin })}
                  style={{ background: formData.isPublishedAdmin ? "var(--success, #22c55e)" : undefined }}
                >
                  {formData.isPublishedAdmin ? "✔ Publié dans Admin" : "Publier dans Admin"}
                </button>
              </div>
            </div>
          )}

          <div className="edit-product-field-group">
            <h4 className="edit-product-label">IMAGES DU PRODUIT</h4>
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>Image devant</h4>
              <div className="add-product-input"><input type="file" accept="image/*" onChange={handleFrontImageChange} /></div>
              {frontImageFile && <div className="field-note" style={{ color: 'var(--success)' }}><UilCheck size={14} /> {frontImageFile.name}</div>}
              {getFrontImage(existingImages) && !frontImageFile && <div className="field-note">Image actuelle: {getFileNameFromUrl(extractImageUrl(getFrontImage(existingImages)))}</div>}
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>Image dos</h4>
              <div className="add-product-input"><input type="file" accept="image/*" onChange={handleBackImageChange} /></div>
              {backImageFile && <div className="field-note" style={{ color: 'var(--success)' }}><UilCheck size={14} /> {backImageFile.name}</div>}
              {getBackImage(existingImages) && !backImageFile && <div className="field-note">Image actuelle: {getFileNameFromUrl(extractImageUrl(getBackImage(existingImages)))}</div>}
            </div>

            <div className="design-gallery-section" style={{ marginTop: '1rem' }}>
              <div className="design-gallery-header">
                <div>
                  <h4>Images additionnelles (galerie)</h4>
                  <div className="field-note">Ajoutez plusieurs images pour la galerie du produit.</div>
                </div>
                <span className="design-gallery-pill">OVH</span>
              </div>
              <div className="add-product-input"><input type="file" accept="image/*" onChange={handleGalleryImageUpload} multiple /></div>

              {galleryImages.length > 0 && (
                <div className="design-gallery-grid" style={{ marginTop: '1rem' }}>
                  {galleryImages.map((img, idx) => {
                    const imgUrl = extractImageUrl(img);
                    return (
                      <div className="design-gallery-card" key={getExistingImageKey(img) || `existing_${idx}`}>
                        <img src={getDisplayImageUrl(imgUrl)} alt={img.originalName || "Galerie"} />
                        <button type="button" className="design-gallery-remove" onClick={() => handleDeleteExistingGalleryImage(getExistingImageKey(img))} title="Supprimer l'image"><UilTrashAlt size={16} /></button>
                        <div className="design-gallery-meta">
                          <span>{idx+1}. {img.originalName || "Image"}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {newGalleryFiles.length > 0 && (
                <div className="design-gallery-grid" style={{ marginTop: '1rem' }}>
                  {newGalleryFiles.map((img, idx) => (
                    <div className="design-gallery-card" key={img.id}>
                      <img src={img.previewUrl} alt={img.name} />
                      <button type="button" className="design-gallery-remove" onClick={() => handleDeleteNewGalleryImage(img.id)} title="Supprimer l'image"><UilTrashAlt size={16} /></button>
                      <div className="design-gallery-meta">
                        <span>Nouveau: {img.name}</span>
                        <small>{formatFileSize(img.size)}</small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedSizes.length > 0 && (
            <div className="edit-product-summary">
              <h4 className="edit-product-label">RÉSUMÉ</h4>
              <div className="edit-product-field-note">👕 Types: {selectedSizeTypes.map(t => t === 'adult' ? 'Adultes' : 'Enfants').join(' + ')}</div>
              <div className="edit-product-field-note">📏 Tailles: {selectedSizes.map(s => getSizeLabel(s)).join(', ')}</div>
              {!formData.isFinishedProduct ? (
                <>
                  <div className="edit-product-field-note">🎨 Couleurs: {Object.keys(formData.colorsAndSizes).length > 0 ? Object.keys(formData.colorsAndSizes).join(', ') : 'Aucune'}</div>
                  <div className="edit-product-field-note">📊 Stock total: {calculateTotalQuantity()} pièces</div>
                </>
              ) : (
                <>
                  <div className="edit-product-field-note">📊 Stock total: {calculateTotalQuantity()} pièces</div>
                  <div className="edit-product-field-note">🏷️ Produit fini - vente directe</div>
                </>
              )}
              <div className="edit-product-field-note">🖼️ Images: {frontImageFile ? '✔️ devant (nouvelle)' : (getFrontImage(existingImages) ? '✔️ devant existante' : '❌ devant')} | {backImageFile ? '✔️ dos (nouvelle)' : (getBackImage(existingImages) ? '✔️ dos existante' : '❌ dos')} | Galerie: {galleryImages.length + newGalleryFiles.length}</div>
            </div>
          )}

          <div className="edit-product-submit-section">
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button className="edit-product-submit-btn" onClick={handleSubmit} disabled={submitting} style={{ flex: 2 }}>{submitting ? "Mise à jour en cours..." : "Mettre à jour le produit"}</button>
              <button type="button" onClick={() => navigate("/products")} style={{ flex: 1, background: "#dc3545", color: "white", padding: "0.95rem", fontSize: "0.95rem", fontWeight: "700", borderRadius: "var(--edit-radius-md)", cursor: "pointer", border: "none" }}>Annuler</button>
            </div>
          </div>
        </div>

        <div className="edit-product-grid-item">
          <div className="edit-product-preview-section">
            <h3 className="edit-product-preview-title">Image devant</h3>
            {frontPreview ? (
              <div className="edit-product-preview-card">
                {!formData.isFinishedProduct && displayedColor ? (
                  <ColorPreviewCanvas ref={frontCanvasRef} imageUrl={frontPreview} color={displayedColor} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onLoad={() => setFrontLoaded(true)} onError={() => setFrontLoaded(true)} />
                ) : (
                  <img src={getDisplayImageUrl(frontPreview)} alt="Product front" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onLoad={() => setFrontLoaded(true)} onError={() => setFrontLoaded(true)} />
                )}
                <div className="edit-product-preview-badge">Devant</div>
              </div>
            ) : (
              <div className="edit-product-placeholder"><p>Aucune image</p></div>
            )}
          </div>

          <div className="edit-product-preview-section">
            <h3 className="edit-product-preview-title">Image dos</h3>
            {backPreview ? (
              <div className="edit-product-preview-card">
                {!formData.isFinishedProduct && displayedColor ? (
                  <ColorPreviewCanvas ref={backCanvasRef} imageUrl={backPreview} color={displayedColor} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onLoad={() => setBackLoaded(true)} onError={() => setBackLoaded(true)} />
                ) : (
                  <img src={getDisplayImageUrl(backPreview)} alt="Product back" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onLoad={() => setBackLoaded(true)} onError={() => setBackLoaded(true)} />
                )}
                <div className="edit-product-preview-badge">Dos</div>
              </div>
            ) : (
              <div className="edit-product-placeholder"><p>Aucune image</p></div>
            )}
          </div>

          {(galleryImages.length > 0 || newGalleryFiles.length > 0) && (
            <div className="edit-product-preview-section">
              <h3 className="edit-product-preview-title">Galerie ({galleryImages.length + newGalleryFiles.length})</h3>
              <div className="edit-product-preview-card" style={{ aspectRatio: 'auto', minHeight: '150px', overflowY: 'auto', padding: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '8px' }}>
                  {galleryImages.map((img) => {
                    const imgUrl = extractImageUrl(img);
                    return <img key={getExistingImageKey(img) || `preview_${img.url}`} src={getDisplayImageUrl(imgUrl)} alt={img.originalName || "Galerie"} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px' }} />;
                  })}
                  {newGalleryFiles.map((img) => (
                    <img key={img.id} src={img.previewUrl} alt={img.name} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px' }} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {displayedColor && !formData.isFinishedProduct && (
            <div className="edit-product-color-info">
              <strong>Couleur sélectionnée:</strong> {displayedColor}
              <div style={{ display: "inline-block", width: "20px", height: "20px", backgroundColor: displayedColor, borderRadius: "50%", marginLeft: "10px", verticalAlign: "middle", border: "1px solid var(--edit-border)" }} />
            </div>
          )}
        </div>
      </div>
      <ToastContainer position="top-right" />
    </div>
  );
};
